import { FC, useContext, useMemo, useState } from 'react'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { requestMediaPermissions, MediaPermissionsErrorType } from 'mic-check'
import jsLogger from 'js-logger'
import { PermissionsPageCustomization } from 'types/internal'
import Background from 'components/Background'
import UserClickContext from 'shared/contexts/UserClickContext'
import LocalizationContext from 'shared/contexts/LocalizationContext'
import { PermissionErrorType } from 'shared/contexts/StreamDataProvider'

type PermissionsFlowProps = {
  lang?: string
  onPermissionsGranted: () => void
  onPermissionsError: (error: PermissionErrorType) => void
  avatarImageUrl?: string
  customization?: PermissionsPageCustomization
}

const PermissionsFlow: FC<PermissionsFlowProps> = ({
  lang = 'en',
  onPermissionsGranted,
  onPermissionsError,
  customization,
  avatarImageUrl
}) => {
  const { getString } = useContext(LocalizationContext)

  const [checkingPermissions, setCheckingPermissions] = useState(false)
  const { setClicked } = useContext(UserClickContext)

  const mainHeading = useMemo(() => {
    let res = getString('conversation_about_to_chat', lang)
    if (customization?.mainHeading) {
      res = customization.mainHeading
    }
    if (
      customization?.mainHeadingTranslates &&
      customization.mainHeadingTranslates[lang]
    ) {
      res = customization.mainHeadingTranslates[lang]
    }
    return res
  }, [customization, lang])

  const subheading = useMemo(() => {
    let res = getString('conversation_about_to_chat', lang)
    if (customization?.subheading) {
      res = customization.subheading
    }
    if (
      customization?.subheadingTranslates &&
      customization.subheadingTranslates[lang]
    ) {
      res = customization.subheadingTranslates[lang]
    }
    return res
  }, [customization, lang])

  const handleRequestPermissions = async () => {
    // if (isTest) {
    //   jsLogger.info(
    //     'PermissionsFlow: isTest is true, bypassing actual permission request.'
    //   )
    //   onPermissionsGranted()
    //   return
    // }
    setClicked(true)

    try {
      jsLogger.debug('PermissionsFlow: permissions_requested')
      setCheckingPermissions(true)
      await requestMediaPermissions()
      jsLogger.debug('PermissionsFlow: permissions_granted')
      setCheckingPermissions(false)
      onPermissionsGranted()
    } catch (error: any) {
      jsLogger.error('PermissionsFlow: Error requesting media permissions: ', {
        error
      })
      setCheckingPermissions(false)
      let specificError = PermissionErrorType.systemDenied // Default
      if (error.type === MediaPermissionsErrorType.Generic) {
        specificError = PermissionErrorType.systemDenied
      } else if (
        error.type === MediaPermissionsErrorType.SystemPermissionDenied
      ) {
        specificError = PermissionErrorType.systemDenied
      } else if (
        error.type === MediaPermissionsErrorType.UserPermissionDenied
      ) {
        specificError = PermissionErrorType.userDenied
      } else if (
        error.type === MediaPermissionsErrorType.CouldNotStartVideoSource
      ) {
        specificError = PermissionErrorType.trackError
      }
      onPermissionsError(specificError)
    }
  }

  const renderButton = () => {
    let buttonText = 'Request permissions'
    if (customization?.buttonText) {
      buttonText = customization?.buttonText
    }
    if (customization?.buttonTextTranslates) {
      const t = customization?.buttonTextTranslates[lang]
      buttonText = t
    }
    return (
      <button
        className='h-16 bg-blue-400 text-white font-medium text-[16px] w-[260px] mt-7 rounded-[4px]'
        onClick={handleRequestPermissions}
        disabled={checkingPermissions}
      >
        {checkingPermissions
          ? getString('conversation_checking_permissions', lang)
          : buttonText}
      </button>
    )
  }

  const renderBackground = () => {
    return (
      <Background
        customization={customization}
        avatarImageUrl={avatarImageUrl}
        addDarkLayout
      />
    )
  }

  return (
    <div
      className={`absolute top-0 left-0 bottom-0 right-0 w-full h-full flex items-center justify-center bg-[#1F2937]`}
      // style={backgroundStyles}
    >
      {renderBackground()}

      <div className='z-10 absolute top-20 left-4 text-gray-300'>
        <div>
          <div style={{ height: '50px', width: '50px' }}>
            <DotLottieReact
              src='https://lottie.host/1ba91606-15d0-4f27-9f17-095c58dfb5ac/IivUjwcaOY.json'
              autoplay
              loop
              backgroundColor='transparent'
              speed={1}
              style={{ width: '100%', height: '100%' }}
              mode='forward'
            />
          </div>
        </div>
        <span className='text-[10px] pt-1'>
          <i>{getString('conversation_volume_up', lang)}</i>
        </span>
      </div>

      {checkingPermissions && (
        <div className='z-10 text-[24px] text-white font-semibold flex flex-col items-center'>
          <span className='drop-shadow-[1px_1px_1px_rgba(0,0,0,1)]'>
            <i>{getString('conversation_initializing', lang)}</i>
          </span>
          <div className='h-1 w-[187px] bg-white mt-[23px]'>
            <div className='h-1 w-[106px] bg-teal-600' />
          </div>
        </div>
      )}

      {!checkingPermissions && (
        <div className='z-10 w-[300px] flex flex-col items-center'>
          <span className='font-semibold text-[24px] text-white drop-shadow-[1px_1px_1px_rgba(0,0,0,1)] text-center leading-8'>
            {mainHeading}
          </span>
          <div className='w-full bg-white mt-10 rounded-xl flex flex-col items-center pt-7 pb-4'>
            <span className='text-[20px] font-semibold text-center leading-7 px-4'>
              {subheading}
            </span>
            {renderButton()}
          </div>
        </div>
      )}
    </div>
  )
}

export default PermissionsFlow
